import { Injectable, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import { UnauthorizedToastComponent } from '../components/unauthorized-toast/unauthorized-toast.component';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'any',
})
export class TokenExpireService implements OnDestroy {
  private readonly TOKEN_TIME: number = 7_200_000;
  private readonly FROM_MESSAGE_TO_EXPIRE: number = 3_700_000;
  private readonly WARNING_MESSAGE_TIME: number =
    this.TOKEN_TIME - this.FROM_MESSAGE_TO_EXPIRE;

  private messageTimer?: ReturnType<typeof setTimeout>;
  private logoutTimer?: ReturnType<typeof setTimeout>;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private toastService: ToastrService,
  ) {}

  ngOnDestroy(): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }
    if (this.messageTimer) {
      clearTimeout(this.messageTimer);
    }

    this.clearInitialTimes();
    this.subscriptions.unsubscribe();
  }

  setTokenExpirationWarning() {
    if (this.messageTimer) {
      if (this.logoutTimer) {
        clearTimeout(this.logoutTimer);
      }
      clearTimeout(this.messageTimer);
      this.clearInitialTimes();
    }

    this.initialMessageTime = this.initialMessageTime
      ? this.initialMessageTime
      : Date.now();

    const executionTime = this.getTimeDifference(
      this.initialMessageTime,
      this.WARNING_MESSAGE_TIME,
    );

    this.messageTimer = setTimeout(() => {
      this.setLogoutTimer();
    }, executionTime);
  }

  private setLogoutTimer() {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.clearInitialTimes();
    }

    this.initialLogoutTime = this.initialLogoutTime
      ? this.initialLogoutTime
      : Date.now();

    const executionTime = this.getTimeDifference(
      this.initialLogoutTime,
      this.FROM_MESSAGE_TO_EXPIRE,
    );

    this.logoutTimer = setTimeout(() => {
      this.logout();
    }, executionTime);
  }

  private logout() {
    //this.authService.logout();
  }

  private showExpirationMessage(title?: string, detail?: string): void {
    const heading = title ? title : 'Your session will expire in 1 minutes.';
    const message = detail
      ? detail
      : "You've been inactive for a while. For your security, we'll automatically sign you out in approximately 1 minutes. Choose 'Stay signed in' to continue or 'Signed Out' if you're done.";

    const options = {
      tapToDismiss: false,
      closeButton: false,
      disableTimeOut: true,
      toastClass: 'ngx-toastr toast-info toast-size xl',
      toastComponent: UnauthorizedToastComponent,
    };

    this.subscriptions.add(
      this.toastService
        .info(message, heading, options)
        .onAction.subscribe((actionName) => {
          if (actionName === 'logout') {
            this.logout();
          } else {
            this.setTokenExpirationWarning();
          }
        }),
    );
  }

  private get initialMessageTime(): number {
    return Number(localStorage.getItem('initialMessageTime'));
  }

  private set initialMessageTime(dateTimeNumber: number) {
    const dateTime = dateTimeNumber.toString();
    localStorage.setItem('initialMessageTime', dateTime);
  }

  private get initialLogoutTime(): number {
    return Number(localStorage.getItem('initialLogoutTime'));
  }

  private set initialLogoutTime(dateTimeNumber: number) {
    const dateTime = dateTimeNumber.toString();
    localStorage.setItem('initialLogoutTime', dateTime);
  }

  private clearInitialTimes() {
    localStorage.removeItem('initialMessageTime');
    localStorage.removeItem('initialLogoutTime');
  }

  getTimeDifference(initialTime: number, execTime: number): number {
    const currentTime = Date.now();
    let executionTime = initialTime + execTime - currentTime;
    if (executionTime < 0) {
      executionTime = 0;
    }

    return executionTime;
  }
}
